import { RefObject, useCallback, useEffect, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, SxProps, Theme, Tooltip } from '@mui/material';

import { copyElementToClipboard } from './copyElementToClipboard';

type CopyButtonProps = Readonly<{
  id: string;
  contentRef: RefObject<HTMLElement>;
}>;

export const CopyButton = ({ id, contentRef }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [displayText, setDisplayText] = useState('Copy');

  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (copied) {
      setDisplayText('Copied!');
    } else if (failed) {
      setDisplayText('Copy failed — check browser permissions');
    } else {
      setHovered(false);
      const timer = setTimeout(() => setDisplayText('Copy'), 200);

      return () => clearTimeout(timer);
    }
  }, [copied, failed]);

  const handleClick = useCallback(async () => {
    if (!contentRef.current) return;

    const success = await copyElementToClipboard(contentRef.current);

    if (success) {
      setFailed(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setFailed(true);
      setTimeout(() => setFailed(false), 3000);
    }
  }, [contentRef]);

  return (
    <Tooltip
      title={displayText}
      placement="top"
      arrow
      open={hovered || copied || failed}
      disableHoverListener
    >
      <Box
        id={id}
        sx={iconButtonSx}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <AppIcon iconName="Copy01" diameter="20px" color="currentColor" />
      </Box>
    </Tooltip>
  );
};

const iconButtonSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: backrs.colors['Gray/500'],
  '&:hover': {
    color: backrs.colors['Brand/500'],
  },
});
